import React, { useEffect, useRef, useState } from 'react';
import {
  Redirect, useHistory, useLocation, useParams,
} from 'react-router';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import Page from '../../../components/Layout/Page';
import handleApiResponse from '../../../utils/handleApiResponse';
import { getStudentById, getStudentContractsByStudentId, getStudentsFromRepresentative } from '../../../requests/api/students';
import StudentDetailHeader from './Components/StudentDetailHeader';
import { StudentData } from '../StudentListView/types';
import StudentDetailNavbar from './Components/StudentDetailNavbar';
import ContractListView from './Views/ContractView/ContractListView';
import SectionsListView from './Views/SectionsView/SectionsListView';
import FeedbacksListView from './Views/FeedbacksView/FeedbacksListView';
import AssistancesView from './Views/AssistancesView/AssistancesListView';
import TestsListView from './Views/TestsView/TestsListView';
import { ReduxState } from '../../../types';
import { STUDENT, TEACHER, REPRESENTATIVE } from '../../../utils/user_types';
import VideosListView from './Views/VideosView/VideosListView';
import RepresentativeView from './Views/RepresentativeView/RepresentativeView';
import CircularProgressComponent from '../../../components/Loading/CircularProgressComponent';
import FileListView from './Views/FilesView';
import StudentInfo from '../../EnrollmentPending/StudentInfo';
import { StudentStage } from '../../../utils/enrollment';
import { ACCEPTED } from '../../../utils/contract';
import { AcademicYearProvider } from './contexts/AcademicYearContext';

interface LocationState {
  activeTab: string;
  studentsUnderRepresentative: number[],
}

function StudentDetailView() {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [student, setStudent] = useState<StudentData>();
  const [activeTab, setActiveTab] = useState(0);
  const [enrollmentTab, setEnrollmentTab] = useState<'informacion' | 'plan-estudios'>('informacion');
  const [updateStudentTrigger, setUpdateStudentTrigger] = useState(false);
  const tabInitializedRef = useRef(false);
  const [showLoading, setShowLoading] = useState(true);
  const [studentsFromRepresentatives, setStudentsFromRepresentatives] = useState<StudentData[]>([]);
  // null mientras no se sabe todavía si el plan de estudios fue aceptado.
  const [studyPlanAccepted, setStudyPlanAccepted] = useState<boolean | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const account = useSelector((state: ReduxState) => state.account);
  const isStudent = account?.user && account.user!.userType === STUDENT;
  const isTeacher = account?.user && account.user!.userType === TEACHER;
  const isRepresentative = account?.user?.userType === REPRESENTATIVE;

  // El apoderado queda retenido en el plan de estudios hasta que lo acepte: no puede
  // navegar al detalle del alumno (contrato, secciones, guías, etc.) antes de eso.
  useEffect(() => {
    if (!isRepresentative || !id) {
      setStudyPlanAccepted(true);
      return;
    }

    const fetchStudyPlanStatus = async () => {
      try {
        const { studentContracts } = await getStudentContractsByStudentId(id);
        // El contrato más reciente (el endpoint ordena por createdAt DESC).
        const [contract] = studentContracts?.contracts || [];
        // Solo retenemos cuando el contrato viene del flujo de plan de estudios y sigue
        // pendiente. Los contratos sin status (creados desde la pestaña Alumnos o previos
        // a la columna) no bloquean la vista.
        const isPending = Boolean(contract?.status) && contract.status !== ACCEPTED;
        setStudyPlanAccepted(!isPending);
      } catch {
        // Si no podemos verificarlo, no bloqueamos la vista.
        setStudyPlanAccepted(true);
      }
    };

    fetchStudyPlanStatus();
  }, [id, isRepresentative, updateStudentTrigger]);

  useEffect(() => {
    const getSpecificStudent = async () => {
      if (account.user && account.user.userType === REPRESENTATIVE) {
        try {
          const { students } = await getStudentsFromRepresentative(account.user.userTypeId);
          const allStudentInformation = students.map((elem: {
            student: StudentData
          }) => ({
            ...elem,
            name: elem.student.user!.name,
            lastname: elem.student.user!.lastname,
            email: elem.student.user!.email,
            phoneNumber: elem.student.phoneNumber,
            rut: elem.student.rut,
            onlineEnabled: elem.student.onlineEnabled,
            student: elem.student,
            stage: elem.student.stage,
          }));
          setStudentsFromRepresentatives(allStudentInformation);
          setStudent(allStudentInformation.find((s: {
            studentId: number,
          }) => s.studentId.toString() === id));
        } catch (err) {
          const e = err as AxiosError;
          handleApiResponse(enqueueSnackbar, e, false);
        }
      } else {
        try {
          const { student: relevantStudent } = await getStudentById(id);

          const allStudentInformation = {
            ...relevantStudent,
            name: relevantStudent.user && relevantStudent.user.name,
            lastname: relevantStudent.user && relevantStudent.user.lastname,
            email: relevantStudent.user && relevantStudent.user.email,
          };

          setStudent(allStudentInformation);
        } catch (err) {
          const e = err as AxiosError;
          handleApiResponse(enqueueSnackbar, e, false);
        }
      }
      setShowLoading(false);
    };
    getSpecificStudent();
  }, [id, updateStudentTrigger]);

  // Al cambiar de alumno, permitir inicializar el tab de nuevo
  useEffect(() => {
    tabInitializedRef.current = false;
  }, [id]);

  useEffect(() => {
    if (location?.state?.activeTab) {
      setActiveTab(parseInt(location.state.activeTab, 10));
    }

    if (location.search && !tabInitializedRef.current) {
      const queryParams = new URLSearchParams(location.search);
      const tabParam = queryParams.get('tab');
      if (tabParam === 'plan-estudios' || tabParam === 'informacion') {
        setEnrollmentTab(tabParam);
        tabInitializedRef.current = true;
      }

      const redirection = queryParams.get('redirection');
      if (redirection) {
        history.replace({ pathname: `/alumnos/${id}`, state: { activeTab: 4 } });
      }
    }
  }, [location]);

  useEffect(() => {
    if ((isStudent || isTeacher) && !location?.state?.activeTab) {
      setActiveTab(1);
    }
  }, [isStudent, isTeacher]);

  // Tab inicial UNA SOLA VEZ según stage (ref evita que se vuelva a ejecutar al cambiar student)
  useEffect(() => {
    if (!student || tabInitializedRef.current) return;

    tabInitializedRef.current = true;

    const stageOrder = [
      StudentStage.ENROLLMENT,
      StudentStage.STUDY_PLAN,
      StudentStage.SCHEDULE,
      StudentStage.ENROLLED,
      StudentStage.PAID,
      StudentStage.COMMENTS,
    ];
    const currentStageIndex = stageOrder.indexOf(student.stage as StudentStage);
    const studyPlanStageIndex = stageOrder.indexOf(StudentStage.STUDY_PLAN);

    if (currentStageIndex >= studyPlanStageIndex && studyPlanStageIndex !== -1) {
      setEnrollmentTab('plan-estudios');
    }
  }, [student]);

  const tabToShow = () => {
    switch (activeTab) {
      case 0:
        if (!(isStudent || isTeacher)) {
          return <ContractListView />;
        }
        return <></>;
      case 1:
        return <SectionsListView
          studentName={(student && student.name) ?? ''}
          studentLastname={(student && student.lastname) ?? ''}
        />;
      case 2:
        return <TestsListView studentOnlineEnabled={student?.onlineEnabled} />;
      case 3:
        return <FeedbacksListView />;
      case 4:
        return <AssistancesView />;
      case 5:
        return <VideosListView />;
      case 6:
        return <RepresentativeView />;
      case 7:
        return <FileListView studentId={student?.id ?? ''} />;
      default:
        return <ContractListView />;
    }
  };

  //  Alumno no puede ver perfil de otros alumnos
  if (account.user && isStudent
    && account.user.userTypeId.toString() !== id) {
    return <Redirect to={`/alumnos/${account.user.userTypeId}`} />;
  }

  // Apoderado no puede ver perfil de otros alumnos que estan bajo su tutela
  if (account?.user
    && account.user!.userType === REPRESENTATIVE) {
    if (studentsFromRepresentatives.length === 0) {
      return <h1>No tienes alumnos asociados</h1>;
    }

    const auxStudentsIds = studentsFromRepresentatives
      .map((elem: StudentData) => Number(elem.studentId));

    if (!auxStudentsIds.includes(Number(id))) {
      return <Redirect to={`/alumnos/${auxStudentsIds[0]}`} />;
    }
  }

  const mustAcceptStudyPlan = isRepresentative && studyPlanAccepted === false;

  const isEnrollmentOrStudyPlanStage = student
    && (mustAcceptStudyPlan
      || student.stage === StudentStage.ENROLLMENT
      || student.stage === StudentStage.STUDY_PLAN);

  return (
    <AcademicYearProvider>
      <Page title="Alumnos">
        {showLoading || (isRepresentative && studyPlanAccepted === null)
          ? <CircularProgressComponent />
          : <>
            {account.user!.userType !== REPRESENTATIVE && student && <StudentDetailHeader
              student={student}
              setUpdateStudentTrigger={setUpdateStudentTrigger}
              userType={(account?.user && account.user!.userType) ?? ''}
            />}
            {account.user!.userType === REPRESENTATIVE && student
              && studentsFromRepresentatives && studentsFromRepresentatives.length > 0
              && <StudentDetailHeader
                student={student}
                students={isEnrollmentOrStudyPlanStage ? undefined : studentsFromRepresentatives}
                setUpdateStudentTrigger={setUpdateStudentTrigger}
                userType={(account?.user && account.user!.userType) ?? ''}
              />}
            {student && (
              (mustAcceptStudyPlan
                || student.stage === StudentStage.ENROLLMENT
                || student.stage === StudentStage.STUDY_PLAN
                || student.stage === StudentStage.COMMENTS)
                ? <StudentInfo
                  tab={enrollmentTab}
                  onTabChange={() => {
                    const newEnrollmentTab = enrollmentTab === 'plan-estudios' ? 'informacion' : 'plan-estudios';
                    setEnrollmentTab(newEnrollmentTab);
                  }}
                  studentIdProp={id}
                  skipAccessCheck
                />
                : <>
                  <StudentDetailNavbar activeTab={activeTab} setActiveTab={setActiveTab} />
                  {tabToShow()}
                </>
            )}
          </>}
      </Page>
    </AcademicYearProvider>
  );
}

export default StudentDetailView;
